import { useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2, Compass, ExternalLink, Flag, Lock, Sparkle, Stamp } from "lucide-react";
import { Rule, roman } from "./Ornament";
import { WaxSeal } from "./WaxSeal";
import { DraftBanner } from "./RouteResult";
import { CONTENT } from "@/lib/catalog";

const Sources = ({ id }) => {
  const c = CONTENT[id];
  if (!c) return null;
  return (
    <div className="mt-6 border-t border-border pt-5" data-testid="step-sources">
      {c.k?.length > 0 && (
        <>
          <span className="eyebrow text-muted-foreground">Key concepts</span>
          <ul className="mt-2 flex flex-wrap gap-2">
            {c.k.map((k) => (
              <li key={k} className="rounded-full border border-border px-3 py-1 text-xs font-display">{k}</li>
            ))}
          </ul>
        </>
      )}
      {c.sources?.length > 0 && (
        <>
          <span className="eyebrow text-muted-foreground block mt-5">Learn from · external sources</span>
          <ul className="mt-2 space-y-2">
            {c.sources.map((s) => (
              <li key={s.url}>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="source-link"
                  className="group flex items-start gap-3 rounded-lg border border-border bg-background/50 p-3 hover:border-primary/50 transition-colors"
                >
                  <ExternalLink className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span className="min-w-0">
                    <span className="font-display text-base leading-tight block group-hover:text-primary transition-colors">{s.title}</span>
                    <span className="text-xs text-muted-foreground block mt-0.5">
                      {s.creator}{s.year ? ` · ${s.year}` : ""} · {s.kind} · {s.access.replace("_", " ")}
                    </span>
                    <span className="text-xs text-muted-foreground/80 block mt-1">{s.why_this_source}</span>
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </>
      )}
      <p className="mt-4 text-[0.7rem] text-muted-foreground font-display italic">
        In-app lessons are not built yet. Atlas points you to curated free sources.
      </p>
    </div>
  );
};

export const LearningView = ({ route, startIndex, sealed, onSeal, onExitToRoute, onRestart }) => {
  const steps = route.steps || [];
  const [i, setI] = useState(Math.min(startIndex || 0, steps.length - 1));
  const [done, setDone] = useState(false);
  const [stamping, setStamping] = useState(false);

  const step = steps[i];
  const isLast = i === steps.length - 1;
  const sealedCount = steps.filter((_, k) => sealed?.has(k)).length;
  const progress = Math.round((sealedCount / steps.length) * 100);
  const alreadySealed = sealed?.has(i);

  const advance = () => {
    if (isLast) setDone(true);
    else setI((n) => n + 1);
  };

  const stampAndAdvance = () => {
    if (stamping) return;
    if (alreadySealed) return advance();
    onSeal(i);
    setStamping(true);
    setTimeout(() => {
      setStamping(false);
      advance();
    }, 950);
  };

  if (done) {
    return (
      <section
        data-testid="learning-complete-state"
        className="max-w-2xl mx-auto px-4 sm:px-6 pt-16 pb-24 text-center atlas-rise"
      >
        <span className="inline-grid place-items-center h-16 w-16 rounded-full bg-primary text-primary-foreground mx-auto mb-7 shadow-[0_0_50px_-10px_hsl(var(--brass-glow)/0.8)]">
          <Flag className="h-7 w-7" strokeWidth={1.6} />
        </span>
        <Rule label="Destination reached" className="max-w-xs mx-auto mb-5" />
        <h2 className="font-display font-medium tracking-tight text-3xl sm:text-4xl leading-[1.05]">
          You’ve arrived at <span className="italic text-primary">{route.destination}</span>
        </h2>
        <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
          Every step on the route is complete. Take a breath — then pick your
          next destination.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <button data-testid="restart-button" onClick={onRestart} className="btn-brass px-6">
            <Compass className="h-4 w-4" /> Plan a new route
          </button>
          <button data-testid="back-to-route-button" onClick={onExitToRoute} className="btn-ghost px-6">
            Review the route
          </button>
        </div>
      </section>
    );
  }

  const hard = step.essential ?? step.kind === "hard";
  const isGoal = step.is_goal ?? isLast;

  return (
    <section
      data-testid="learning-view"
      className="max-w-2xl mx-auto px-4 sm:px-6 pt-8 pb-24 atlas-rise"
    >
      <button
        data-testid="learning-back-button"
        onClick={onExitToRoute}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-8 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Back to route
      </button>
      {route.draft && <div className="mb-6"><DraftBanner compact /></div>}

      {/* Progress */}
      <div className="flex items-center justify-between mb-2.5">
        <span className="eyebrow text-muted-foreground">
          Step <span className="numeral text-primary text-sm">{roman(i + 1)}</span> of{" "}
          <span className="numeral text-sm">{roman(steps.length)}</span>
        </span>
        <span className="eyebrow text-muted-foreground">
          <span className="numeral text-primary text-sm">{roman(sealedCount)}</span> sealed · {progress}%
        </span>
      </div>
      <div className="h-px w-full bg-border relative overflow-visible">
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2 h-[3px] rounded-full bg-primary transition-all duration-700 shadow-[0_0_12px_hsl(var(--brass-glow)/0.6)]"
          style={{ width: `${progress}%` }}
          data-testid="learning-progress-bar"
        />
      </div>

      {/* seal strip */}
      <div data-testid="seal-strip" className="mt-4 flex items-center gap-2 flex-wrap">
        {steps.map((_, k) =>
          sealed?.has(k) ? (
            <WaxSeal key={k} className="h-7 w-7 seal-pop" label={`Step ${k + 1} sealed`} />
          ) : (
            <span
              key={k}
              className={`grid place-items-center h-7 w-7 rounded-full border numeral text-[0.65rem] ${
                k === i ? "border-primary text-primary" : "border-border text-muted-foreground"
              }`}
            >
              {roman(k + 1)}
            </span>
          )
        )}
      </div>

      <div className="plate-frame card-ink relative mt-8 p-7 sm:p-10 overflow-hidden">
        {(stamping || alreadySealed) && (
          <WaxSeal
            stamping={stamping}
            className="absolute top-5 right-5 h-16 w-16 sm:h-20 sm:w-20 z-10"
            label="Step sealed"
          />
        )}
        <div className="flex items-center justify-between gap-4 mb-5">
          <span className="numeral text-primary text-3xl leading-none">{roman(i + 1)}</span>
          {!isGoal ? (
            <span
              className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[0.66rem] font-semibold font-mono uppercase tracking-[0.16em]"
              style={{
                color: `hsl(var(--${hard ? "hard" : "soft"}))`,
                background: `hsl(var(--${hard ? "hard-bg" : "soft-bg"}))`,
                borderColor: `hsl(var(--${hard ? "hard" : "soft"}) / 0.35)`,
              }}
            >
              {hard ? <Lock className="h-3 w-3" /> : <Sparkle className="h-3 w-3" />}
              {hard ? "Essential prerequisite" : "Recommended"}
            </span>
          ) : (
            <span className="eyebrow text-primary">Destination</span>
          )}
        </div>
        <h2 className="font-display font-medium tracking-tight text-2xl sm:text-4xl leading-[1.08]">
          {step.title}
        </h2>
        <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
          {step.detail}
        </p>
        {!route.draft && <Sources id={step.id} />}
      </div>

      <button
        data-testid="learning-continue-button"
        onClick={stampAndAdvance}
        disabled={stamping}
        className="btn-brass mt-7 w-full sm:w-auto px-6 py-3.5 text-base"
      >
        {stamping ? (
          <>
            <Stamp className="h-5 w-5" /> Sealing…
          </>
        ) : alreadySealed ? (
          <>
            {isLast ? "Finish route" : "Continue"} <ArrowRight className="h-5 w-5" />
          </>
        ) : isLast ? (
          <>
            <CheckCircle2 className="h-5 w-5" /> Seal &amp; finish route
          </>
        ) : (
          <>
            <Stamp className="h-5 w-5" /> Seal this step &amp; continue
          </>
        )}
      </button>
    </section>
  );
};
